/**
 * Enums and constants shared by every worktree.
 *
 * These are the single source of truth. Do not re-declare tiers, audit actions,
 * statuses or the NDA version anywhere else in the repo.
 */
/* -------------------------------------------------------------------------- */
/* Document tiers                                                             */
/* -------------------------------------------------------------------------- */
/**
 * Tier 1: framework overview and certification summaries. Released on email
 * verification alone.
 *
 * Tier 2: SOC 2 report, penetration test, detailed policies. Released only
 * after a click-to-accept NDA has been recorded for that customer and email.
 */
export const DOCUMENT_TIER = {
    TIER_1: 1,
    TIER_2: 2,
};
export const DOCUMENT_TIERS = [
    DOCUMENT_TIER.TIER_1,
    DOCUMENT_TIER.TIER_2,
];
/** Tier 2 requires a recorded NDA acceptance before any access is granted. */
export const NDA_REQUIRED_TIERS = [DOCUMENT_TIER.TIER_2];
export function isDocumentTier(value) {
    return value === DOCUMENT_TIER.TIER_1 || value === DOCUMENT_TIER.TIER_2;
}
/* -------------------------------------------------------------------------- */
/* Customer status                                                            */
/* -------------------------------------------------------------------------- */
export const CUSTOMER_STATUS = {
    ACTIVE: 'active',
    SUSPENDED: 'suspended',
    EXPIRED: 'expired',
};
export const CUSTOMER_STATUSES = [
    CUSTOMER_STATUS.ACTIVE,
    CUSTOMER_STATUS.SUSPENDED,
    CUSTOMER_STATUS.EXPIRED,
];
/* -------------------------------------------------------------------------- */
/* Auth token purpose                                                         */
/* -------------------------------------------------------------------------- */
/**
 * What a row in auth_tokens is for. All three are short lived and hashed at
 * rest. They do not share a consumption rule:
 *
 * - `otp` and `magic_link` are single use. Once `consumed_at` is set the row is
 *   dead, and a second presentation of the same value is a failure.
 * - `document_delivery` is not single use. See DELIVERY_TOKEN docs on the
 *   portal.getDocumentContent route: a PDF viewer fetches one document over
 *   many requests, and burning the token on the first would break the viewer.
 *   For this purpose `consumed_at` remains null and every successful fetch gets
 *   its own audit row.
 *
 * Applying the single use rule to all three fails closed: documents stop
 * loading after the first request. That is a broken viewer, not an exposure.
 * Applying the delivery rule to OTP fails open. Never generalise in that
 * direction.
 */
export const AUTH_TOKEN_PURPOSE = {
    OTP: 'otp',
    MAGIC_LINK: 'magic_link',
    DOCUMENT_DELIVERY: 'document_delivery',
};
export const AUTH_TOKEN_PURPOSES = [
    AUTH_TOKEN_PURPOSE.OTP,
    AUTH_TOKEN_PURPOSE.MAGIC_LINK,
    AUTH_TOKEN_PURPOSE.DOCUMENT_DELIVERY,
];
/* -------------------------------------------------------------------------- */
/* Audit actions                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Every value written to audit_log.action must come from this list. The log is
 * append only: rows are never updated or deleted.
 */
export const AUDIT_ACTION = {
    /* Customer portal authentication */
    OTP_REQUESTED: 'otp.requested',
    OTP_VERIFIED: 'otp.verified',
    OTP_REJECTED: 'otp.rejected',
    SESSION_CREATED: 'session.created',
    /* Customer portal usage */
    PORTAL_VIEWED: 'portal.viewed',
    DOCUMENTS_LISTED: 'documents.listed',
    NDA_ACCEPTED: 'nda.accepted',
    /** A delivery token was issued by portal.createDocumentUrl. */
    DOCUMENT_URL_MINTED: 'document.url_minted',
    /** Bytes left the server during one successful delivery-token verification. */
    DOCUMENT_DELIVERED: 'document.delivered',
    DOCUMENT_WATERMARKED: 'document.watermarked',
    ACCESS_DENIED: 'access.denied',
    /* Admin, authenticated by Cloudflare Access */
    /**
     * Admin reads. The customer list carries every customer name, and the user
     * list carries every authorised address, so reading them is a disclosure in
     * its own right and gets a row like any other access. Ownership of these
     * writes sits with the route, exactly as it does for the admin writes below:
     * the repository and the security core write nothing here, so one request
     * puts one row in the log.
     */
    ADMIN_CUSTOMERS_LISTED: 'admin.customers.listed',
    ADMIN_CUSTOMER_READ: 'admin.customer.read',
    ADMIN_USERS_LISTED: 'admin.users.listed',
    ADMIN_DOCUMENTS_LISTED: 'admin.documents.listed',
    ADMIN_ARCHIVED_DOCUMENTS_LISTED: 'admin.archived_documents.listed',
    ADMIN_DOCUMENT_READ: 'admin.document.read',
    ADMIN_CUSTOMER_DOCUMENTS_LISTED: 'admin.customer_documents.listed',
    ADMIN_REQUEST_ITEMS_LISTED: 'admin.request_items.listed',
    ADMIN_CUSTOMER_CREATED: 'admin.customer.created',
    ADMIN_CUSTOMER_UPDATED: 'admin.customer.updated',
    ADMIN_CUSTOMER_DELETED: 'admin.customer.deleted',
    ADMIN_USER_CREATED: 'admin.user.created',
    ADMIN_USER_DELETED: 'admin.user.deleted',
    ADMIN_DOCUMENT_CREATED: 'admin.document.created',
    ADMIN_DOCUMENT_UPDATED: 'admin.document.updated',
    ADMIN_DOCUMENT_DELETED: 'admin.document.deleted',
    ADMIN_DOCUMENTS_MAPPED: 'admin.documents.mapped',
    ADMIN_REQUEST_ITEM_CREATED: 'admin.request_item.created',
    ADMIN_REQUEST_ITEM_UPDATED: 'admin.request_item.updated',
    ADMIN_REQUEST_ITEM_DELETED: 'admin.request_item.deleted',
    ADMIN_AUDIT_LOG_READ: 'admin.audit_log.read',
    /* Sandbox access: credentials and links */
    /** The portal read of a user's links and credential metadata. */
    SANDBOX_ACCESS_LISTED: 'sandbox_access.listed',
    ADMIN_CREDENTIALS_LISTED: 'admin.credentials.listed',
    ADMIN_LINKS_LISTED: 'admin.links.listed',
    CREDENTIAL_CREATED: 'credential.created',
    /** The secret left the server once, to its recipient. */
    CREDENTIAL_REVEALED: 'credential.revealed',
    /**
     * A reveal the route refused: no current NDA, or the burn matched no row
     * (not this recipient's, already revealed, expired or revoked). Denials the
     * security core makes on the session or the customer are its own
     * `access.denied` rows, not these.
     */
    CREDENTIAL_REVEAL_DENIED: 'credential.reveal_denied',
    CREDENTIAL_REVOKED: 'credential.revoked',
    /**
     * The daily cron nulled the ciphertext of an expired or revoked row. One row
     * per credential. Purges that happen inside a customer delete or suspend are
     * covered by that action's own row, and a credential removed by the cascade
     * from a user deletion is covered by `admin.user.deleted`.
     */
    CREDENTIAL_PURGED: 'credential.purged',
    LINK_CREATED: 'link.created',
    LINK_REORDERED: 'link.reordered',
    LINK_DELETED: 'link.deleted',
};
export const AUDIT_ACTIONS = Object.values(AUDIT_ACTION);
export function isAuditAction(value) {
    return typeof value === 'string' && AUDIT_ACTIONS.includes(value);
}
/* -------------------------------------------------------------------------- */
/* Sandbox credentials                                                        */
/* -------------------------------------------------------------------------- */
/**
 * What a credential grants. `api` is a client id and secret for the sandbox
 * API; `login` is a username and password for the sandbox UI.
 */
export const CREDENTIAL_KIND = {
    API: 'api',
    LOGIN: 'login',
};
export const CREDENTIAL_KINDS = [
    CREDENTIAL_KIND.API,
    CREDENTIAL_KIND.LOGIN,
];
/**
 * The only environment a credential may be issued for, enforced by a CHECK
 * constraint. The portal cannot verify that the pasted secret really is a
 * sandbox one; that is an operator control, confirmed on the create form.
 */
export const CREDENTIAL_ENVIRONMENT = 'sandbox';
/**
 * Non-secret details shown plainly to the recipient beside the masked secret:
 * an ordered list of label and value rows, whatever the credential bundle
 * needs (a URL, a username, a client id). The kind does not decide which rows
 * appear; the operator does.
 */
export const CREDENTIAL_MAX_DETAILS = 8;
/** Upper bound on a detail row's label, in characters, after trimming. */
export const CREDENTIAL_DETAIL_LABEL_MAX = 40;
/** Upper bound on a detail row's value, in characters, after trimming. */
export const CREDENTIAL_DETAIL_VALUE_MAX = 500;
/** Upper bound on the label shown beside the secret, in characters. */
export const CREDENTIAL_SECRET_LABEL_MAX = 40;
/** What the secret is called when the operator does not say. */
export const CREDENTIAL_DEFAULT_SECRET_LABEL = {
    api: 'Client secret',
    login: 'Password',
};
/** Default lifetime of an unrevealed secret, in days. */
export const CREDENTIAL_DEFAULT_TTL_DAYS = 7;
/**
 * Longest lifetime an operator may set, in days. A credential also never
 * outlives its customer's own expiry, whichever comes first.
 */
export const CREDENTIAL_MAX_TTL_DAYS = 30;
/** Upper bound on the plaintext secret, in characters. */
export const CREDENTIAL_MAX_SECRET_LENGTH = 4096;
/* -------------------------------------------------------------------------- */
/* NDA                                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Current NDA text version. Bump this when the NDA wording changes; existing
 * acceptances keep the version they were recorded against, and a customer whose
 * latest acceptance is an older version must accept again before Tier 2 access.
 */
export const NDA_VERSION = '2026-01';
/* -------------------------------------------------------------------------- */
/* Access control timings                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Pre-signed R2 URL lifetime, in seconds. Short by design.
 *
 * Also the TTL of a document delivery token, so that the two mechanisms behind
 * portal.getDocumentContent expire on the same schedule and a customer cannot
 * tell them apart by how long a link keeps working.
 */
export const SIGNED_URL_TTL_SECONDS = 300;
/**
 * Largest PDF, in bytes, that may be watermarked at access time.
 *
 * Stamping happens in the Worker, in memory, at the moment of access: the
 * source bytes, the pdf-lib document and the output all exist at once, inside a
 * request that has a hard memory ceiling and a wall clock limit. A large enough
 * PDF does not stamp slowly, it fails the request outright, and it fails at the
 * moment a customer is trying to read our SOC 2 report.
 *
 * So the check belongs at upload, not at access. Admin document upload must
 * reject a file above this size when `watermark` is set, and say so plainly,
 * rather than accepting it and leaving a document that can never be opened.
 * A Tier 2 document too large to stamp needs a different answer, not a larger
 * limit here.
 */
export const MAX_WATERMARK_BYTES = 10 * 1024 * 1024;
/* -------------------------------------------------------------------------- */
/* Fixed copy                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * The auth email step must always answer with this, whether or not the address
 * is authorised. Anything else leaks account existence.
 */
export const AUTH_GENERIC_RESPONSE_MESSAGE = 'If authorised, a code has been sent.';
/*
 * There is deliberately no COMPLIANCE_STATEMENT here any more, and nothing
 * replaces it. The portal and the console make no compliance claim in their
 * own chrome: that content belongs in the documents the portal serves, where
 * it is versioned, tiered and audited like everything else we say about
 * security. A sentence in a footer is none of those things. The copy lint in
 * each frontend now enforces the absence rather than the presence.
 */
//# sourceMappingURL=constants.js.map